import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function FAQ() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How long does it take to see results from social media marketing?',
      answer: 'Most clients start seeing measurable improvements in engagement within 4-6 weeks. Significant growth in followers and conversions typically occurs within 3-6 months. We provide monthly reports so you can track progress every step of the way.'
    },
    {
      question: 'Which social media platforms do you specialise in?',
      answer: 'We specialise in Instagram, TikTok, LinkedIn, Facebook, and X (Twitter). Our team stays current with platform algorithms and trends to ensure your content performs optimally on each channel.'
    },
    {
      question: 'Do you create all the content, or do we need to provide it?',
      answer: 'We handle everything! Our team creates custom graphics, writes engaging captions, sources or creates videos, and schedules all posts. You can also provide brand assets or ideas if you prefer to be involved in the creative process.'
    },
    {
      question: 'How do you measure success?',
      answer: 'We track key metrics including follower growth, engagement rate, reach, website clicks, and conversions. You\'ll receive detailed monthly reports with insights and recommendations for continuous improvement.'
    },
    {
      question: 'Can I cancel my plan at any time?',
      answer: 'Yes, all our plans are flexible with no long-term contracts. We require 30 days\' notice for cancellation. We\'re confident you\'ll love the results, but we never want to lock you into something that doesn\'t work for your business.'
    },
    {
      question: 'What makes unuu different from other agencies?',
      answer: 'unuu is a fully post-AI business. We have taken the time to properly understand how AI can be used to improve speed, consistency, and quality, without losing the human thinking that good marketing still needs. Unlike many agencies who are either not using AI at all, or quietly using it to reduce their own costs, we use AI openly and responsibly to save time and then pass those efficiencies directly on to you. What we do is not rocket science. We simply help people find you when they need you. That means faster delivery, consistent output, and fair, transparent pricing. You benefit from smarter systems, not bigger invoices.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      ref={ref}
      id="faq"
      className={`py-20 bg-gradient-to-b from-white to-gray-50 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <h2 className="text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about our social media marketing services
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${openIndex === index ? 'shadow-lg' : 'hover:shadow-md'}`}
              style={{ transitionDelay: isVisible ? `${index * 80}ms` : '0ms' }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
              >
                <span className="text-lg font-semibold text-slate-900 pr-4">
                  {faq.question}
                </span>
                <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#00989e] to-[#00b4a6] flex-shrink-0 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}>
                  <i className="ri-arrow-down-s-line text-xl text-white"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[600px]' : 'max-h-0'
                }`}
              >
                <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-12 text-center transition-all duration-700 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00989e] to-[#00b4a6] text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-[#00989e]/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-1-line"></i>
            Get in Touch
          </a>
        </div>
      </div>
    </section>
  );
}
